import React, { useEffect, useState } from 'react';
import { UserProfile } from '../../../utils/interfaces';

interface GeneralSectionProps {
  editableProfile: UserProfile;
  handleProfileChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  isOpen: boolean;
  setInfoModalType?: (type: 'wrist-to-floor' | 'swing-type' | null) => void;
}

const GeneralSection: React.FC<GeneralSectionProps> = ({ editableProfile, handleProfileChange, isOpen, setInfoModalType }) => {
  const [modalRenderKey, setModalRenderKey] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (isOpen) setModalRenderKey(k => k + 1);
  }, [isOpen]);

  // Check if advanced fields are filled and expand by default
  useEffect(() => {
    if (editableProfile.general?.height_inches || 
        editableProfile.general?.wrist_to_floor_inches || 
        editableProfile.general?.swing_speed || 
        editableProfile.general?.swinging_type ||
        editableProfile.general?.tempo ||
        editableProfile.general?.course_conditions) {
      setShowAdvanced(true);
    }
  }, [editableProfile.general]);

  return (
    <div className="profile-form-grid general-section" key={modalRenderKey}>
      <div className="club-card">
        <div className="form-field">
          <label htmlFor="general.gender">Gender</label>
          <select id="general.gender" name="general.gender" value={editableProfile.general?.gender || ''} onChange={handleProfileChange}>
            <option value="" disabled>Select...</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="general.handedness">Handedness</label>
          <select id="general.handedness" name="general.handedness" value={editableProfile.general?.handedness || ''} onChange={handleProfileChange}>
            <option value="" disabled>Select...</option>
            <option value="Right">Right</option>
            <option value="Left">Left</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="general.handicap">Handicap</label>
          <input type="number" id="general.handicap" name="general.handicap" value={editableProfile.general?.handicap || ''} onChange={handleProfileChange} placeholder="e.g., 15" />
        </div>
        <div className="form-field">
          <label htmlFor="general.playing_frequency">Playing <br />Frequency</label>
          <select id="general.playing_frequency" name="general.playing_frequency" value={editableProfile.general?.playing_frequency || ''} onChange={handleProfileChange}>
            <option value="" disabled>Select...</option>
            <option value="Less than once a month">Less than once a month</option>
            <option value="1-2 times per month">1-2 times per month</option>
            <option value="1-2 times per week">1-2 times per week</option>
            <option value="3+ times per week">3+ times per week</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="general.skill_level">Skill Level</label>
          <select id="general.skill_level" name="general.skill_level" value={editableProfile.general?.skill_level || ''} onChange={handleProfileChange}>
            <option value="" disabled>Select...</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="general.preferred_brands">Preferred <br />Brands</label>
          <input type="text" id="general.preferred_brands" name="general.preferred_brands" value={editableProfile.general?.preferred_brands || ''} onChange={handleProfileChange} placeholder="e.g., TaylorMade, Callaway" maxLength={20} />
        </div>

        {/* Advanced section toggle */}
        <div 
          className="advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            cursor: 'pointer',
            padding: '12px 0',
            borderTop: '1px solid #e0e0e0',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: '#666',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <span>Advanced Options</span>
          <span style={{ 
            transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {/* Advanced fields - conditionally rendered */}
        {showAdvanced && (
          <div style={{ marginTop: '16px' }}>
            <div className="form-field">
              <label htmlFor="general.height_inches">Height (in)</label>
              <input
                type="number"
                id="general.height_inches"
                name="general.height_inches"
                value={editableProfile.general?.height_inches || ''}
                onChange={handleProfileChange}
                placeholder="e.g., 70"
              />
            </div>
            <div className="form-field">
              <label htmlFor="general.wrist_to_floor_inches">
                Wrist-to-Floor <br />(inches)
                {setInfoModalType && (
                  <span 
                    className="info-icon"
                    onClick={() => setInfoModalType('wrist-to-floor')}
                  >
                    i
                  </span>
                )}
              </label>
              <input
                type="number"
                id="general.wrist_to_floor_inches"
                name="general.wrist_to_floor_inches"
                value={editableProfile.general?.wrist_to_floor_inches || ''}
                onChange={handleProfileChange}
                placeholder="e.g., 34"
              />
            </div>
            <div className="form-field">
              <label htmlFor="general.swing_speed">Driver Swing <br />Speed (mph)</label>
              <input
                type="number"
                id="general.swing_speed"
                name="general.swing_speed"
                value={editableProfile.general?.swing_speed || ''}
                onChange={handleProfileChange}
                placeholder="e.g., 95"
              />
            </div>
            <div className="form-field">
              <label htmlFor="general.swinging_type">
                Swing <br />Type
                {setInfoModalType && (
                  <span 
                    className="info-icon"
                    onClick={() => setInfoModalType('swing-type')}
                  >
                    i
                  </span>
                )}
              </label>
              <select
                id="general.swinging_type"
                name="general.swinging_type"
                value={editableProfile.general?.swinging_type || ''}
                onChange={handleProfileChange}
              >
                <option value="" disabled>Select...</option>
                <option value="digger">Digger</option>
                <option value="neutral">Neutral</option>
                <option value="sweeper">Sweeper</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="general.tempo">Tempo</label>
              <select
                id="general.tempo"
                name="general.tempo"
                value={editableProfile.general?.tempo || ''}
                onChange={handleProfileChange}
              >
                <option value="" disabled>Select...</option>
                <option value="slow">Slow</option>
                <option value="moderate">Moderate</option>
                <option value="fast">Fast</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="general.course_conditions">Course <br />Conditions</label>
              <select
                id="general.course_conditions"
                name="general.course_conditions"
                value={editableProfile.general?.course_conditions || ''}
                onChange={handleProfileChange}
              >
                <option value="" disabled>Select...</option>
                <option value="firm">Firm</option>
                <option value="soft">Soft</option>
                <option value="mixed">Mixed</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSection;
